/**
 * Cashback Enrollment Component
 * Handles enrollment/unenrollment in the cashback program via Shopify Customer API
 * Uses form submission approach that Shopify Flow can watch
 */

class CashbackEnrollment extends HTMLElement {
  constructor() {
    super();
    this.customerId = this.dataset.customerId;
    this.customerEmail = this.dataset.customerEmail;
    this.customerName = this.dataset.customerName;
    this.toggleButton = null;
    this.messageEl = null;
  }

  connectedCallback() {
    this.toggleButton = this.querySelector('.cashback-enrollment__toggle');
    this.messageEl = this.querySelector('.cashback-enrollment__message');

    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', this.handleToggle.bind(this));
    }
  }

  async handleToggle(event) {
    const button = event.currentTarget;
    const action = button.dataset.action;
    const isEnrolling = action === 'enroll';

    button.disabled = true;
    button.textContent = isEnrolling ? 'Enrolling...' : 'Opting out...';

    try {
      // Use Shopify Customer Account API to add/remove tag
      // This requires the customer to be logged in
      // Note: Customer account form doesn't directly support tags, so Flow needs to watch
      // for contact form submissions or customer updates to add/remove the tag
      const formData = new URLSearchParams();
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');
      formData.append('customer[tags]', isEnrolling 
        ? 'cashback-enrolled' 
        : '' // Remove tag via separate request - Flow will handle
      );

      const response = await fetch('/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (!response.ok) {
        // Fallback: Customer account form may not accept tags directly
        // Use contact form submission that Flow can watch and process
        return this.handleViaContactForm(isEnrolling, button);
      }

      this.showMessage(
        isEnrolling 
          ? 'Enrolled! You will earn 10% cashback on future purchases.'
          : 'Unenrolled from cashback program.',
        'success'
      );

      setTimeout(() => window.location.reload(), 2000);

    } catch (error) {
      // If account form submission fails, use contact form fallback
      this.handleViaContactForm(isEnrolling, button);
    }
  }

  async handleViaContactForm(isEnrolling, button) {
    try {
      // Fallback: Use contact form submission that Flow can watch
      // Flow should be configured to watch contact form submissions with these subjects
      // and add/remove the "cashback-enrolled" tag accordingly
      const formData = new URLSearchParams();
      formData.append('form_type', 'contact');
      formData.append('utf8', '✓');
      formData.append('contact[email]', this.customerEmail || '');
      formData.append('contact[name]', this.customerName || '');
      formData.append('contact[body]', isEnrolling 
        ? 'CASHBACK_ENROLL_REQUEST'
        : 'CASHBACK_UNENROLL_REQUEST'
      );
      formData.append('contact[subject]', isEnrolling 
        ? 'Cashback Enrollment Request'
        : 'Cashback Unenrollment Request'
      );

      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (!response.ok) throw new Error('Failed to submit request');

      this.showMessage(
        isEnrolling 
          ? 'Enrollment request submitted! Flow will process your enrollment and you will be enrolled shortly. Please refresh in a moment.'
          : 'Unenrollment request submitted! Flow will process your request.',
        'success'
      );

      setTimeout(() => window.location.reload(), 3000);

    } catch (error) {
      console.error('Cashback enrollment error:', error);
      this.showMessage('Error. Please contact support.', 'error');
      button.disabled = false;
      button.textContent = isEnrolling 
        ? 'Enroll Now' 
        : 'Opt Out';
    }
  }

  showMessage(text, type) {
    if (!this.messageEl) return;

    this.messageEl.textContent = text;
    this.messageEl.dataset.type = type;
    this.messageEl.hidden = false;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.messageEl.hidden = true;
    }, 5000);
  }
}

// Register custom element
if (!customElements.get('cashback-enrollment')) {
  customElements.define('cashback-enrollment', CashbackEnrollment);
}

